import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";

import { makeStyles, useTheme, alpha } from "@material-ui/core/styles";
import { green, red, orange, grey, blue } from "@material-ui/core/colors";
import {
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  Paper,
  IconButton,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
} from "@material-ui/core";
import {
  Edit,
  DeleteOutline,
  Send,
  Sync,
  Add,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Block,
  ExpandMore,
  Help,
  Visibility,
  Settings,
  VerifiedUser,
  Phone,
  Link as LinkIcon,
  Image,
  Close,
  Check,
  AddCircleOutline,
  Reply,
  Videocam,
  InsertDriveFile,
  TouchApp,
  CloudUpload,
  Publish,
  PlayCircleFilled,
  Search,
  FilterList,
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import TemplateValidationPanel from "../../components/TemplateValidationPanel";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { validateTemplate, extractVariables, LIMITS } from "../../utils/templateValidator";
import { normalizeMediaUrl } from "../../utils/mediaUrl";
import { isDarkTheme, tintStyle } from "../../helpers/darkTint";

// Cores do WhatsApp no tema escuro, usadas na pré-visualização
const WA_DARK = {
  background: "#0b141a",
  bubble: "#005c4b",
  link: "#53bdeb",
  divider: "rgba(255,255,255,0.1)",
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  statusChip: {
    fontWeight: "bold",
    fontSize: "0.75rem",
  },
  modalContent: {
    padding: 0,
  },
  formSection: {
    padding: theme.spacing(2),
  },
  sampleSection: {
    backgroundColor: isDarkTheme(theme) ? alpha(orange[500], 0.08) : "#fffde7",
    border: `1px solid ${isDarkTheme(theme) ? alpha(orange[500], 0.4) : orange[200]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  sampleTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
    color: isDarkTheme(theme) ? orange[300] : orange[900],
    fontWeight: 600,
  },
  previewContainer: {
    backgroundColor: isDarkTheme(theme) ? WA_DARK.background : "#e5ddd5",
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c8c4bf' fill-opacity='0.15'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  previewBubble: {
    backgroundColor: isDarkTheme(theme) ? WA_DARK.bubble : "#dcf8c6",
    borderRadius: "0 12px 12px 12px",
    padding: theme.spacing(1.5),
    maxWidth: 350,
    marginLeft: "auto",
    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  },
  previewHeader: {
    fontWeight: 700,
    marginBottom: 4,
    borderBottom: `1px solid ${isDarkTheme(theme) ? WA_DARK.divider : "rgba(0,0,0,0.08)"}`,
    paddingBottom: 4,
  },
  previewFooter: {
    marginTop: 6,
    paddingTop: 4,
    borderTop: `1px solid ${isDarkTheme(theme) ? WA_DARK.divider : "rgba(0,0,0,0.08)"}`,
  },
  categoryHelp: {
    backgroundColor: isDarkTheme(theme) ? alpha("#fff", 0.05) : theme.palette.grey[50],
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : theme.palette.grey[200]}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  categoryHelpTitle: {
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: theme.spacing(0.5),
    fontSize: "0.8rem",
  },
  categoryHelpText: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  charCount: {
    float: "right",
    fontSize: "0.7rem",
  },
  tabPanel: {
    padding: theme.spacing(2, 0),
  },
  varChip: {
    margin: 2,
    ...tintStyle(isDarkTheme(theme), blue),
    fontFamily: "monospace",
    fontSize: "0.75rem",
  },
  validationSidebar: {
    position: "sticky",
    top: 0,
  },
  dialogPaper: {
    maxHeight: "92vh",
  },
  buttonsSection: {
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    backgroundColor: isDarkTheme(theme) ? alpha("#fff", 0.05) : grey[50],
  },
  buttonRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    padding: "8px 0",
    borderBottom: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[200]}`,
    "&:last-child": { borderBottom: "none" },
  },
  buttonTypeChip: {
    minWidth: 100,
    fontSize: "0.7rem",
  },
  headerFormatSelector: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  headerFormatOption: {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    border: `2px solid ${isDarkTheme(theme) ? grey[600] : grey[300]}`,
    borderRadius: 8,
    transition: "all 0.2s",
    minWidth: 80,
    "&:hover": { borderColor: blue[300], backgroundColor: isDarkTheme(theme) ? alpha(blue[500], 0.12) : blue[50] },
  },
  headerFormatOptionActive: {
    borderColor: `${blue[500]} !important`,
    backgroundColor: `${isDarkTheme(theme) ? alpha(blue[500], 0.2) : blue[50]} !important`,
    boxShadow: `0 0 0 1px ${blue[500]}`,
  },
  previewButton: {
    display: "block",
    textAlign: "center",
    padding: "8px 16px",
    marginTop: 4,
    borderTop: `1px solid ${isDarkTheme(theme) ? WA_DARK.divider : "rgba(0,0,0,0.08)"}`,
    color: isDarkTheme(theme) ? WA_DARK.link : blue[600],
    fontWeight: 500,
    fontSize: "0.85rem",
  },
  previewMediaPlaceholder: {
    backgroundColor: isDarkTheme(theme) ? "rgba(0,0,0,0.2)" : grey[200],
    borderRadius: "8px 8px 0 0",
    height: 150,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
  },
  variableTypeBox: {
    border: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : grey[300]}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    backgroundColor: isDarkTheme(theme) ? alpha("#fff", 0.05) : "#f5f5ff",
  },
  variableTypeLabel: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: isDarkTheme(theme) ? theme.palette.text.primary : grey[800],
    marginBottom: 4,
  },
  variableTypeRadio: {
    "& .MuiFormControlLabel-label": {
      fontSize: "0.82rem",
    },
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  filterControl: {
    minWidth: 160,
    backgroundColor: theme.palette.background.paper,
  },
  filterTextInput: {
    minWidth: 220,
    backgroundColor: theme.palette.background.paper,
  },
}));

const STATUS_COLORS = {
  APPROVED: { bg: green[100], color: green[800], icon: <CheckCircle fontSize="small" /> },
  REJECTED: { bg: red[100], color: red[800], icon: <ErrorIcon fontSize="small" /> },
  PENDING: { bg: orange[100], color: orange[800], icon: <HourglassEmpty fontSize="small" /> },
  PAUSED: { bg: grey[200], color: grey[700], icon: <Block fontSize="small" /> },
  DISABLED: { bg: grey[200], color: grey[600], icon: <Block fontSize="small" /> },
};

const CATEGORIES = [
  { value: "UTILITY", label: "Utilitário" },
  { value: "MARKETING", label: "Marketing" },
  { value: "AUTHENTICATION", label: "Autenticação" },
];

const CATEGORIES_FOR_CREATION = CATEGORIES.filter((c) => c.value !== "AUTHENTICATION");

const CATEGORY_DESCRIPTIONS = {
  UTILITY: {
    title: "Utilitário",
    desc: "Para mensagens transacionais disparadas por ação do usuário: confirmações de pedido, atualizações de entrega, alertas de conta, lembretes de agendamento, pesquisas de satisfação sobre pedidos específicos.",
    examples: "Ex: 'Seu pedido {{numero_pedido}} foi confirmado', 'Lembrete: seu agendamento é dia {{data}}'",
    warning: "NÃO use para conteúdo promocional — será reclassificado como Marketing.",
  },
  MARKETING: {
    title: "Marketing",
    desc: "Para promoções, ofertas, recomendações, retargeting, promoção de app e construção de relacionamento. Inclui conteúdo misto (utilitário + promoção).",
    examples: "Ex: 'Aproveite {{desconto}}% de desconto!', 'Sua assinatura expira em {{data}}, renove com desconto'",
    warning: "Inclua opt-out e saudação personalizada para melhor engajamento.",
  },
  AUTHENTICATION: {
    title: "Autenticação",
    desc: "Apenas para verificação de identidade com código OTP. Use templates da Biblioteca de Templates da Meta. Sem URLs, emojis ou mídia.",
    examples: "Ex: '{{codigo}} é seu código de verificação'",
    warning: "Parâmetros limitados a 15 caracteres. O mais restritivo dos 3 tipos.",
  },
};

const LANGUAGES = [
  { value: "pt_BR", label: "Português (Brasil)" },
  { value: "en_US", label: "English (US)" },
  { value: "es", label: "Español" },
  { value: "es_AR", label: "Español (Argentina)" },
  { value: "es_MX", label: "Español (México)" },
  { value: "en", label: "English" },
];

const HEADER_FORMATS = [
  { value: "NONE", label: "Nenhum", icon: null },
  { value: "TEXT", label: "Texto", icon: Edit },
  { value: "IMAGE", label: "Imagem", icon: Image },
  { value: "VIDEO", label: "Vídeo", icon: Videocam },
  { value: "DOCUMENT", label: "Documento", icon: InsertDriveFile },
];

const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Resposta Rápida", icon: <Reply style={{ fontSize: 16 }} /> },
  { value: "URL", label: "Link / URL", icon: <LinkIcon style={{ fontSize: 16 }} /> },
  { value: "PHONE_NUMBER", label: "Telefone", icon: <Phone style={{ fontSize: 16 }} /> },
];

// Render preview text with variable samples highlighted
const renderPreviewText = (text, sampleValues, isDark = false) => {
  if (!text) return null;
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return parts.map((part, i) => {
    const match = part.match(/^\{\{([^}]+)\}\}$/);
    if (match) {
      const varName = match[1].trim();
      const sample = sampleValues[varName];
      if (sample) {
        return (
          <span key={i} style={{
            ...tintStyle(isDark, blue),
            fontWeight: 600,
            borderRadius: 3,
            padding: "0 3px"
          }}>
            {sample}
          </span>
        );
      }
      return (
        <span key={i} style={{
          ...tintStyle(isDark, orange),
          fontStyle: "italic",
          borderRadius: 3,
          padding: "0 3px"
        }}>
          {`[${varName}]`}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

// Modal para criar/editar template
const TemplateModal = ({ open, onClose, template, whatsapps, onSave }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const mutedColor = isDark ? theme.palette.text.secondary : grey[600];
  const warningTextColor = isDark ? orange[300] : orange[800];
  const [formData, setFormData] = useState({
    name: "",
    displayName: "",
    language: "pt_BR",
    category: "UTILITY",
    wabaId: "",
    bodyText: "",
    headerText: "",
    headerFormat: "NONE",
    headerMediaUrl: "",
    footerText: "",
    buttons: [],
    variableType: "positional",
  });
  const [sampleValues, setSampleValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [showCategoryHelp, setShowCategoryHelp] = useState(false);
  const [newButtonType, setNewButtonType] = useState("QUICK_REPLY");
  const [uploading, setUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const normalizedHeaderMediaUrl = useMemo(
    () => normalizeMediaUrl(formData.headerMediaUrl),
    [formData.headerMediaUrl]
  );

  // Auto-detect variable type from body text
  const detectedVarType = useMemo(() => {
    const vars = extractVariables(formData.bodyText || "");
    if (vars.length === 0) return null;
    const hasNamed = vars.some(v => !/^\d+$/.test(v));
    return hasNamed ? "named" : "positional";
  }, [formData.bodyText]);

  // Sync variableType when detection changes and user hasn't manually set it
  useEffect(() => {
    if (detectedVarType && detectedVarType !== formData.variableType) {
      setFormData(prev => ({ ...prev, variableType: detectedVarType }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectedVarType]);

  // Extract variables from all text fields (excluding button URL vars - those are per-button)
  const allVariables = useMemo(() => {
    const bodyVars = extractVariables(formData.bodyText || "");
    const headerVars = formData.headerFormat === "TEXT" ? extractVariables(formData.headerText || "") : [];
    return [...new Set([...headerVars, ...bodyVars])];
  }, [formData.bodyText, formData.headerText, formData.headerFormat]);

  // Run validation dynamically
  const validation = useMemo(() => {
    return validateTemplate({
      name: formData.name || "",
      category: formData.category || "UTILITY",
      language: formData.language || "pt_BR",
      bodyText: formData.bodyText || "",
      headerText: formData.headerFormat === "TEXT" ? (formData.headerText || "") : "",
      headerFormat: formData.headerFormat || "NONE",
      headerMediaUrl: formData.headerMediaUrl || "",
      footerText: formData.footerText || "",
      buttons: formData.buttons || [],
      sampleValues,
    });
  }, [formData, sampleValues]);

  useEffect(() => {
    if (template) {
      const bodyComp = template.components?.find((c) => c.type === "BODY");
      const headerComp = template.components?.find((c) => c.type === "HEADER");
      const footerComp = template.components?.find((c) => c.type === "FOOTER");
      const buttonsComp = template.components?.find((c) => c.type === "BUTTONS");

      const headerFormat = headerComp?.format || (headerComp ? "TEXT" : "NONE");

      const loadedButtons = (buttonsComp?.buttons || []).map(b => ({
        type: b.type || "QUICK_REPLY",
        text: b.text || "",
        url: b.url || "",
        phoneNumber: b.phone_number || "",
        urlExample: (b.example && b.example[0]) || "",
      }));

      setFormData({
        name: template.name || "",
        displayName: template.displayName || "",
        language: template.language || "pt_BR",
        category: template.category || "UTILITY",
        wabaId: template.wabaId || "",
        bodyText: bodyComp?.text || template.bodyText || "",
        headerText: headerComp?.text || "",
        headerFormat,
        headerMediaUrl: headerComp?.example?.header_handle?.[0] || "",
        footerText: footerComp?.text || "",
        buttons: loadedButtons,
        variableType: bodyComp?.example?.body_text_named_params ? "named" : "positional",
      });

      // Try to extract existing sample values from component examples
      const existingSamples = {};
      if (bodyComp?.example?.body_text?.[0]) {
        const vars = extractVariables(bodyComp?.text || "");
        bodyComp.example.body_text[0].forEach((val, idx) => {
          if (vars[idx]) existingSamples[vars[idx]] = val;
        });
      }
      if (bodyComp?.example?.body_text_named_params) {
        bodyComp.example.body_text_named_params.forEach(p => {
          existingSamples[p.param_name] = p.example;
        });
      }
      setSampleValues(existingSamples);
    } else {
      setFormData({
        name: "",
        displayName: "",
        language: "pt_BR",
        category: "UTILITY",
        wabaId: "",
        bodyText: "",
        headerText: "",
        headerFormat: "NONE",
        headerMediaUrl: "",
        footerText: "",
        buttons: [],
        variableType: "positional",
      });
      setSampleValues({});
    }
    setActiveTab(0);
    setUploadedFileName("");
  }, [template, open]);

  // Update sample values when variables change
  useEffect(() => {
    setSampleValues(prev => {
      const updated = {};
      allVariables.forEach(v => {
        updated[v] = prev[v] || "";
      });
      return updated;
    });
  }, [allVariables]);

  const handleSampleChange = useCallback((varName, value) => {
    setSampleValues(prev => ({ ...prev, [varName]: value }));
  }, []);

  const handleAddButton = useCallback(() => {
    if ((formData.buttons || []).length >= LIMITS.MAX_BUTTONS) {
      toast.warn(`Máximo de ${LIMITS.MAX_BUTTONS} botões permitidos`);
      return;
    }
    const newBtn = { type: newButtonType, text: "" };
    if (newButtonType === "URL") { newBtn.url = ""; newBtn.urlExample = ""; }
    if (newButtonType === "PHONE_NUMBER") newBtn.phoneNumber = "";
    setFormData(prev => ({ ...prev, buttons: [...(prev.buttons || []), newBtn] }));
  }, [newButtonType, formData.buttons]);

  const handleRemoveButton = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index),
    }));
  }, []);

  const handleButtonChange = useCallback((index, field, value) => {
    setFormData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) =>
        i === index ? { ...btn, [field]: value } : btn
      ),
    }));
  }, []);

  // Upload de mídia para o servidor
  const handleMediaUpload = useCallback(async (file) => {
    if (!file) return;

    // Validar tipo
    const format = formData.headerFormat;
    const allowedTypes = {
      IMAGE: ["image/jpeg", "image/png", "image/webp"],
      VIDEO: ["video/mp4", "video/3gpp"],
      DOCUMENT: ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
    };
    const maxSizes = {
      IMAGE: 5 * 1024 * 1024,
      VIDEO: 16 * 1024 * 1024,
      DOCUMENT: 100 * 1024 * 1024,
    };

    if (allowedTypes[format] && !allowedTypes[format].includes(file.type)) {
      toast.error(`Tipo de arquivo não permitido para ${format}. Tipos aceitos: ${allowedTypes[format].map(t => t.split('/')[1]).join(', ')}`);
      return;
    }
    if (maxSizes[format] && file.size > maxSizes[format]) {
      toast.error(`Arquivo muito grande. Máximo para ${format}: ${Math.round(maxSizes[format] / 1024 / 1024)}MB`);
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/message-templates/media-upload", fd);
      setFormData(prev => ({ ...prev, headerMediaUrl: data.url }));
      setUploadedFileName(data.originalName || file.name);
      toast.success(`Arquivo "${file.name}" enviado com sucesso`);
    } catch (err) {
      toastError(err);
    }
    setUploading(false);
  }, [formData.headerFormat]);

  const handleSave = async () => {
    if (!formData.name || !formData.bodyText || !formData.wabaId) {
      toast.error("Preencha os campos obrigatórios: Nome, Corpo da Mensagem e Conta Empresarial");
      return;
    }

    // Validar se o header com mídia tem URL
    if ((formData.headerFormat === "IMAGE" || formData.headerFormat === "VIDEO" || formData.headerFormat === "DOCUMENT") && !formData.headerMediaUrl) {
      toast.error(`Modelos com o tipo de cabeçalho ${formData.headerFormat} precisam de um exemplo/modelo. Por favor, faça upload de um arquivo de mídia.`);
      return;
    }

    // Validar botões URL
    const urlButtons = (formData.buttons || []).filter(b => b.type === "URL");
    for (let i = 0; i < urlButtons.length; i++) {
      const btn = urlButtons[i];
      if (!btn.url || !btn.url.trim()) {
        toast.error(`Botão URL #${i + 1}: informe a URL.`);
        return;
      }
      if (!/^https?:\/\/.+/i.test(btn.url.replace(/\{\{[^}]+\}\}/g, "exemplo"))) {
        toast.error(`Botão URL #${i + 1}: a URL deve começar com https:// e ser válida.`);
        return;
      }
      const urlVars = extractVariables(btn.url);
      if (urlVars.length > 0 && (!btn.urlExample || !btn.urlExample.trim())) {
        toast.error(`Botão URL #${i + 1}: URL com variável dinâmica requer um valor de exemplo.`);
        return;
      }
    }

    // Warn if score is low
    if (validation.score < 50 && validation.summary.critical > 0) {
      const confirm = window.confirm(
        `Probabilidade de aprovação: ${validation.score}%\n\n` +
        `Existem ${validation.summary.critical} problema(s) crítico(s) que provavelmente causarão rejeição.\n\n` +
        `Deseja enviar mesmo assim?`
      );
      if (!confirm) return;
    }

    // Build components in Meta API format
    const components = [];

    // HEADER component
    if (formData.headerFormat === "TEXT" && formData.headerText) {
      const headerVars = extractVariables(formData.headerText);
      const headerComp = {
        type: "HEADER",
        format: "TEXT",
        text: formData.headerText,
      };
      if (headerVars.length > 0) {
        headerComp.example = {
          header_text: [headerVars.map(v => sampleValues[v] || `[${v}]`)],
        };
      }
      components.push(headerComp);
    } else if ((formData.headerFormat === "IMAGE" || formData.headerFormat === "VIDEO" || formData.headerFormat === "DOCUMENT") && formData.headerMediaUrl) {
      // Só adiciona o componente HEADER de mídia se houver URL (já validado acima)
      const headerComp = {
        type: "HEADER",
        format: formData.headerFormat,
        example: {
          header_handle: [formData.headerMediaUrl],
        },
      };
      components.push(headerComp);
    }

    const bodyVars = extractVariables(formData.bodyText);
    const bodyComp = {
      type: "BODY",
      text: formData.bodyText,
    };

    // Add example/sample values for variables using the selected variable type
    if (bodyVars.length > 0) {
      if (formData.variableType === "named") {
        bodyComp.example = {
          body_text_named_params: bodyVars.map(v => ({
            param_name: v,
            example: sampleValues[v] || `[${v}]`,
          })),
        };
      } else {
        bodyComp.example = {
          body_text: [bodyVars.map(v => sampleValues[v] || `[${v}]`)],
        };
      }
    }
    components.push(bodyComp);

    if (formData.footerText) {
      components.push({
        type: "FOOTER",
        text: formData.footerText,
      });
    }

    // BUTTONS component
    if (formData.buttons && formData.buttons.length > 0) {
      const metaButtons = formData.buttons.map(btn => {
        if (btn.type === "QUICK_REPLY") {
          return { type: "QUICK_REPLY", text: btn.text };
        }
        if (btn.type === "URL") {
          const btnObj = { type: "URL", text: btn.text, url: btn.url };
          // If URL contains variables, add per-button example values
          const urlVars = extractVariables(btn.url || "");
          if (urlVars.length > 0 && btn.urlExample) {
            btnObj.example = [btn.urlExample];
          }
          return btnObj;
        }
        if (btn.type === "PHONE_NUMBER") {
          return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phoneNumber };
        }
        return { type: btn.type, text: btn.text };
      });
      components.push({ type: "BUTTONS", buttons: metaButtons });
    }

    setSaving(true);
    try {
      if (template?.id) {
        await api.put(`/message-templates/${template.id}`, {
          components,
          displayName: formData.displayName,
        });
        toast.success("Template atualizado e reenviado para aprovação");
      } else {
        await api.post("/message-templates", {
          name: formData.name.toLowerCase().replace(/\s+/g, "_"),
          displayName: formData.displayName || formData.name,
          language: formData.language,
          category: formData.category,
          components,
          wabaId: formData.wabaId,
        });
        toast.success("Template criado e enviado para aprovação da Meta");
      }
      onSave();
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const cloudApiWhatsapps = whatsapps.filter((w) => w.type === "cloudapi");

  // Agrupar conexões por WABA (Business Account)
  const wabaGroups = useMemo(() => {
    const groups = {};
    cloudApiWhatsapps.forEach((w) => {
      const waba = w.cloudApiBusinessAccountId;
      if (!waba) return;
      if (!groups[waba]) {
        groups[waba] = { wabaId: waba, connections: [] };
      }
      groups[waba].connections.push(w);
    });
    return Object.values(groups);
  }, [cloudApiWhatsapps]);

  const categoryInfo = CATEGORY_DESCRIPTIONS[formData.category] || CATEGORY_DESCRIPTIONS.UTILITY;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {template ? "Editar Template de Mensagem" : "Novo Template de Mensagem"}
          </Box>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {validation.score > 0 && (
              <Chip
                size="small"
                icon={validation.score >= 70 ? <CheckCircle /> : <ErrorIcon />}
                label={`${validation.score}% aprovação`}
                style={{
                  ...tintStyle(isDark, validation.score >= 70 ? green : validation.score >= 50 ? orange : red),
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers style={{ padding: 0 }}>
        <Grid container>
          {/* Left side: Form */}
          <Grid item xs={12} md={7} style={{ borderRight: `1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}` }}>
            <Box p={2}>
              <Tabs
                value={activeTab}
                onChange={(e, v) => setActiveTab(v)}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                style={{ marginBottom: 16 }}
              >
                <Tab icon={<Settings style={{ fontSize: 18 }} />} label="Configuração" />
                <Tab icon={<Edit style={{ fontSize: 18 }} />} label="Conteúdo" />
                <Tab icon={<Visibility style={{ fontSize: 18 }} />} label="Preview" />
              </Tabs>

              {/* Tab 0: Configuration */}
              {activeTab === 0 && (
                <Box className={classes.tabPanel}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nome do Template *"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        disabled={!!template}
                        helperText="Letras minúsculas, números e underscores (ex: confirmacao_pedido)"
                        inputProps={{ maxLength: LIMITS.NAME_MAX }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Nome de Exibição"
                        value={formData.displayName}
                        onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        helperText="Nome amigável exibido na interface (ex: Confirmação de Pedido)"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Conta Empresarial (WABA) *</InputLabel>
                        <Select
                          value={formData.wabaId}
                          onChange={(e) => setFormData({ ...formData, wabaId: e.target.value })}
                          label="Conta Empresarial (WABA) *"
                          disabled={!!template}
                        >
                          {wabaGroups.map((g) => (
                            <MenuItem key={g.wabaId} value={g.wabaId}>
                              {g.connections.map(c => c.name).join(", ")} ({g.connections.length} número{g.connections.length > 1 ? "s" : ""})
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Categoria *</InputLabel>
                        <Select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          label="Categoria *"
                          disabled={!!template}
                        >
                          {(template ? CATEGORIES : CATEGORIES_FOR_CREATION).map((c) => (
                            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth variant="outlined" margin="dense">
                        <InputLabel>Idioma *</InputLabel>
                        <Select
                          value={formData.language}
                          onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                          label="Idioma *"
                          disabled={!!template}
                        >
                          {LANGUAGES.map((l) => (
                            <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Category Help Box */}
                    <Grid item xs={12}>
                      <Box className={classes.categoryHelp}>
                        <Typography className={classes.categoryHelpTitle}>
                          <Help style={{ fontSize: 16, color: blue[600] }} />
                          {categoryInfo.title} — Quando usar
                        </Typography>
                        <Typography className={classes.categoryHelpText}>
                          {categoryInfo.desc}
                        </Typography>
                        <Typography className={classes.categoryHelpText} style={{ marginTop: 4, fontStyle: "italic" }}>
                          {categoryInfo.examples}
                        </Typography>
                        <Typography className={classes.categoryHelpText} style={{ marginTop: 4, color: warningTextColor, fontWeight: 500 }}>
                          {categoryInfo.warning}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 1: Content */}
              {activeTab === 1 && (
                <Box className={classes.tabPanel}>
                  <Grid container spacing={2}>
                    {/* Header Format Selector */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600 }}>
                        Cabeçalho (Opcional)
                      </Typography>
                      <Box className={classes.headerFormatSelector}>
                        {HEADER_FORMATS.map(fmt => {
                          const Icon = fmt.icon;
                          return (
                            <Box
                              key={fmt.value}
                              className={`${classes.headerFormatOption} ${formData.headerFormat === fmt.value ? classes.headerFormatOptionActive : ""}`}
                              onClick={() => setFormData({ ...formData, headerFormat: fmt.value, headerText: fmt.value !== "TEXT" ? "" : formData.headerText, headerMediaUrl: fmt.value === "TEXT" || fmt.value === "NONE" ? "" : formData.headerMediaUrl })}
                            >
                              {Icon && <Icon style={{ fontSize: 24, color: formData.headerFormat === fmt.value ? (isDark ? blue[300] : blue[600]) : (isDark ? grey[400] : grey[600]), marginBottom: 2 }} />}
                              <Typography variant="caption" style={{ fontWeight: formData.headerFormat === fmt.value ? 600 : 400, color: formData.headerFormat === fmt.value ? (isDark ? blue[200] : blue[800]) : (isDark ? theme.palette.text.secondary : grey[700]), fontSize: "0.7rem" }}>
                                {fmt.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>

                      {/* TEXT header */}
                      {formData.headerFormat === "TEXT" && (
                        <TextField
                          label="Texto do Cabeçalho"
                          value={formData.headerText}
                          onChange={(e) => {
                            if (e.target.value.length <= LIMITS.HEADER_TEXT_MAX) {
                              setFormData({ ...formData, headerText: e.target.value });
                            }
                          }}
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          helperText={
                            <span>
                              Título em negrito no topo. Máximo 1 variável.
                              <span className={classes.charCount} style={{
                                color: formData.headerText.length > LIMITS.HEADER_TEXT_MAX * 0.8 ? orange[700] : mutedColor,
                              }}>
                                {formData.headerText.length}/{LIMITS.HEADER_TEXT_MAX}
                              </span>
                            </span>
                          }
                        />
                      )}

                      {/* IMAGE header */}
                      {formData.headerFormat === "IMAGE" && (
                        <Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                              component="label"
                              disabled={uploading}
                            >
                              {uploading ? "Enviando..." : "Enviar Imagem"}
                              <input
                                type="file"
                                hidden
                                accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => handleMediaUpload(e.target.files[0])}
                              />
                            </Button>
                            <Typography variant="caption" style={{ color: mutedColor }}>
                              ou informe a URL abaixo
                            </Typography>
                          </Box>
                          {uploadedFileName && formData.headerFormat === "IMAGE" && (
                            <Chip
                              size="small"
                              label={uploadedFileName}
                              onDelete={() => { setUploadedFileName(""); setFormData(prev => ({ ...prev, headerMediaUrl: "" })); }}
                              style={{ marginBottom: 8, ...tintStyle(isDark, green) }}
                              icon={<CheckCircle style={{ color: green[600] }} />}
                            />
                          )}
                          <TextField
                            label="URL da Imagem de Amostra"
                            value={formData.headerMediaUrl}
                            onChange={(e) => setFormData({ ...formData, headerMediaUrl: e.target.value })}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            placeholder="https://exemplo.com/imagem.jpg"
                            helperText="Formatos: JPG, PNG, WebP. Máx 5MB."
                            InputProps={{
                              startAdornment: <Image style={{ fontSize: 20, color: grey[500], marginRight: 8 }} />,
                            }}
                          />
                          {formData.headerMediaUrl && /^https?:\/\/.+/i.test(formData.headerMediaUrl) && (
                            <Box mt={1} style={{ maxWidth: 250, position: "relative" }}>
                              <img
                                src={formData.headerMediaUrl}
                                alt="Preview"
                                style={{ width: "100%", borderRadius: 8, border: `1px solid ${grey[300]}` }}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* VIDEO header */}
                      {formData.headerFormat === "VIDEO" && (
                        <Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                              component="label"
                              disabled={uploading}
                            >
                              {uploading ? "Enviando..." : "Enviar Vídeo"}
                              <input
                                type="file"
                                hidden
                                accept="video/mp4,video/3gpp"
                                onChange={(e) => handleMediaUpload(e.target.files[0])}
                              />
                            </Button>
                            <Typography variant="caption" style={{ color: mutedColor }}>
                              ou informe a URL abaixo
                            </Typography>
                          </Box>
                          {uploadedFileName && formData.headerFormat === "VIDEO" && (
                            <Chip
                              size="small"
                              label={uploadedFileName}
                              onDelete={() => { setUploadedFileName(""); setFormData(prev => ({ ...prev, headerMediaUrl: "" })); }}
                              style={{ marginBottom: 8, ...tintStyle(isDark, green) }}
                              icon={<CheckCircle style={{ color: green[600] }} />}
                            />
                          )}
                          <TextField
                            label="URL do Vídeo de Amostra"
                            value={formData.headerMediaUrl}
                            onChange={(e) => setFormData({ ...formData, headerMediaUrl: e.target.value })}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            placeholder="https://exemplo.com/video.mp4"
                            helperText="Formato: MP4, 3GPP. Máx 16MB."
                            InputProps={{
                              startAdornment: <Videocam style={{ fontSize: 20, color: grey[500], marginRight: 8 }} />,
                            }}
                          />
                          {formData.headerMediaUrl && /^https?:\/\/.+/i.test(formData.headerMediaUrl) && (
                            <Box mt={1} style={{ maxWidth: 300 }}>
                              <video
                                src={formData.headerMediaUrl}
                                style={{ width: "100%", borderRadius: 8, border: `1px solid ${grey[300]}` }}
                                controls
                                preload="metadata"
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            </Box>
                          )}
                        </Box>
                      )}

                      {/* DOCUMENT header */}
                      {formData.headerFormat === "DOCUMENT" && (
                        <Box>
                          <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={uploading ? <CircularProgress size={16} /> : <CloudUpload />}
                              component="label"
                              disabled={uploading}
                            >
                              {uploading ? "Enviando..." : "Enviar Documento"}
                              <input
                                type="file"
                                hidden
                                accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={(e) => handleMediaUpload(e.target.files[0])}
                              />
                            </Button>
                            <Typography variant="caption" style={{ color: mutedColor }}>
                              ou informe a URL abaixo
                            </Typography>
                          </Box>
                          {uploadedFileName && formData.headerFormat === "DOCUMENT" && (
                            <Chip
                              size="small"
                              label={uploadedFileName}
                              onDelete={() => { setUploadedFileName(""); setFormData(prev => ({ ...prev, headerMediaUrl: "" })); }}
                              style={{ marginBottom: 8, ...tintStyle(isDark, green) }}
                              icon={<CheckCircle style={{ color: green[600] }} />}
                            />
                          )}
                          <TextField
                            label="URL do Documento de Amostra"
                            value={formData.headerMediaUrl}
                            onChange={(e) => setFormData({ ...formData, headerMediaUrl: e.target.value })}
                            fullWidth
                            variant="outlined"
                            margin="dense"
                            placeholder="https://exemplo.com/documento.pdf"
                            helperText="Formatos: PDF, DOC, DOCX, XLS, XLSX. Máx 100MB."
                            InputProps={{
                              startAdornment: <InsertDriveFile style={{ fontSize: 20, color: grey[500], marginRight: 8 }} />,
                            }}
                          />
                          {formData.headerMediaUrl && /^https?:\/\/.+/i.test(formData.headerMediaUrl) && (
                            <Box mt={1} display="flex" alignItems="center" style={{ gap: 8, padding: 8, backgroundColor: isDark ? alpha("#fff", 0.05) : grey[100], borderRadius: 8 }}>
                              <InsertDriveFile style={{ color: red[400], fontSize: 32 }} />
                              <Box>
                                <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                                  {uploadedFileName || "Documento"}
                                </Typography>
                                <Typography variant="caption" style={{ color: mutedColor }}>
                                  {formData.headerMediaUrl.split('/').pop()}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Corpo da Mensagem *"
                        value={formData.bodyText}
                        onChange={(e) => setFormData({ ...formData, bodyText: e.target.value })}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        multiline
                        rows={8}
                        helperText={
                          <span>
                            Use {"{{1}}, {{2}}"} (posicional) ou {"{{nome}}, {{pedido}}"} (nomeado) para variáveis dinâmicas.
                            <span className={classes.charCount} style={{
                              color: formData.bodyText.length > LIMITS.BODY_MAX * 0.8 ? orange[700] : 
                                     formData.bodyText.length > LIMITS.BODY_MAX ? red[700] : mutedColor,
                              fontWeight: formData.bodyText.length > LIMITS.BODY_MAX ? 700 : 400,
                            }}>
                              {formData.bodyText.length}/{LIMITS.BODY_MAX}
                            </span>
                          </span>
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Rodapé (Opcional)"
                        value={formData.footerText}
                        onChange={(e) => {
                          if (e.target.value.length <= LIMITS.FOOTER_MAX) {
                            setFormData({ ...formData, footerText: e.target.value });
                          }
                        }}
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        helperText={
                          <span>
                            Texto pequeno no final (nome da empresa, opt-out, etc.)
                            <span className={classes.charCount} style={{
                              color: formData.footerText.length > LIMITS.FOOTER_MAX * 0.8 ? orange[700] : mutedColor,
                            }}>
                              {formData.footerText.length}/{LIMITS.FOOTER_MAX}
                            </span>
                          </span>
                        }
                      />
                    </Grid>

                    {/* Variable Samples Section */}
                    {allVariables.length > 0 && (
                      <Grid item xs={12}>
                        <Box className={classes.sampleSection}>
                          <Typography className={classes.sampleTitle}>
                            <VerifiedUser style={{ fontSize: 18 }} />
                            Textos de Amostra para Variáveis (Obrigatório)
                          </Typography>

                          {/* Variable Type Selector */}
                          <Box className={classes.variableTypeBox}>
                            <FormControl component="fieldset">
                              <FormLabel component="legend" className={classes.variableTypeLabel}>
                                Tipo de Variável — informado à Meta no cadastro do template
                              </FormLabel>
                              <RadioGroup
                                row
                                value={formData.variableType}
                                onChange={(e) => setFormData({ ...formData, variableType: e.target.value })}
                                className={classes.variableTypeRadio}
                              >
                                <FormControlLabel
                                  value="positional"
                                  control={<Radio color="primary" size="small" />}
                                  label={
                                    <Box>
                                      <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                        Posicional (Número)
                                      </Typography>
                                      <Typography variant="caption" style={{ color: mutedColor, display: "block", lineHeight: 1.2 }}>
                                        {"Usa {{1}}, {{2}}, {{3}} — formato clássico"}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                <FormControlLabel
                                  value="named"
                                  control={<Radio color="primary" size="small" />}
                                  label={
                                    <Box>
                                      <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                                        Nomeado (Nome)
                                      </Typography>
                                      <Typography variant="caption" style={{ color: mutedColor, display: "block", lineHeight: 1.2 }}>
                                        {"Usa {{nome}}, {{pedido}} — mais descritivo"}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </RadioGroup>
                            </FormControl>
                            {detectedVarType && detectedVarType !== formData.variableType && (
                              <Typography variant="caption" style={{ display: "block", marginTop: 4, color: warningTextColor, fontWeight: 500 }}>
                                As variáveis no texto parecem ser do tipo <strong>{detectedVarType === "named" ? "Nomeado" : "Posicional"}</strong>, mas o tipo selecionado é <strong>{formData.variableType === "named" ? "Nomeado" : "Posicional"}</strong>. Verifique se está correto.
                              </Typography>
                            )}
                          </Box>

                          <Typography variant="caption" style={{ display: "block", marginBottom: 12, color: warningTextColor }}>
                            Templates SEM textos de amostra são frequentemente rejeitados pela Meta.
                            Forneça exemplos realistas de como cada variável será preenchida.
                          </Typography>
                          <Grid container spacing={1}>
                            {allVariables.map((varName) => (
                              <Grid item xs={12} sm={6} key={varName}>
                                <TextField
                                  label={`Amostra para {{${varName}}}`}
                                  value={sampleValues[varName] || ""}
                                  onChange={(e) => handleSampleChange(varName, e.target.value)}
                                  fullWidth
                                  variant="outlined"
                                  margin="dense"
                                  size="small"
                                  placeholder={
                                    /^\d+$/.test(varName)
                                      ? `Ex: ${["Maria", "12345", "10/03/2026", "R$ 99,90", "Av. Brasil 100"][parseInt(varName) - 1] || "valor exemplo"}`
                                      : `Ex: valor de ${varName}`
                                  }
                                  error={!sampleValues[varName]?.trim()}
                                  helperText={!sampleValues[varName]?.trim() ? "Preencha com um valor de exemplo realista" : ""}
                                  InputProps={{
                                    startAdornment: (
                                      <Chip
                                        className={classes.varChip}
                                        label={`{{${varName}}}`}
                                        size="small"
                                        style={{ marginRight: 8 }}
                                      />
                                    ),
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Grid>
                    )}

                    {/* Buttons Section */}
                    <Grid item xs={12}>
                      <Divider style={{ margin: "8px 0" }} />
                      <Box className={classes.buttonsSection}>
                        <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                          <TouchApp style={{ fontSize: 18, color: blue[600] }} />
                          Botões (Opcional)
                          <Typography component="span" variant="caption" style={{ color: mutedColor, marginLeft: 4 }}>
                            {(formData.buttons || []).length}/{LIMITS.MAX_BUTTONS}
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                          Adicione botões interativos à mensagem: resposta rápida, links ou telefone. Não misture Respostas Rápidas com botões de URL/Telefone.
                        </Typography>

                        {/* Existing buttons */}
                        {(formData.buttons || []).map((btn, idx) => (
                          <Box key={idx} className={classes.buttonRow}>
                            <Chip
                              size="small"
                              icon={BUTTON_TYPES.find(t => t.value === btn.type)?.icon}
                              label={BUTTON_TYPES.find(t => t.value === btn.type)?.label || btn.type}
                              className={classes.buttonTypeChip}
                              color="primary"
                              variant="outlined"
                            />
                            <Box flex={1}>
                              <Grid container spacing={1}>
                                <Grid item xs={btn.type === "QUICK_REPLY" ? 12 : 6}>
                                  <TextField
                                    label="Texto do botão"
                                    value={btn.text}
                                    onChange={(e) => handleButtonChange(idx, "text", e.target.value)}
                                    fullWidth
                                    variant="outlined"
                                    margin="dense"
                                    size="small"
                                    inputProps={{ maxLength: LIMITS.BUTTON_TEXT_MAX }}
                                    helperText={`${(btn.text || "").length}/${LIMITS.BUTTON_TEXT_MAX}`}
                                  />
                                </Grid>
                                {btn.type === "URL" && (
                                  <>
                                    <Grid item xs={6}>
                                      <TextField
                                        label="URL"
                                        value={btn.url}
                                        onChange={(e) => handleButtonChange(idx, "url", e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        margin="dense"
                                        size="small"
                                        placeholder="https://site.com/pagina/{{1}}"
                                        helperText="Use {{1}} para URL dinâmica. Ex: https://loja.com/pedido/{{1}}"
                                      />
                                    </Grid>
                                    {extractVariables(btn.url || "").length > 0 && (
                                      <Grid item xs={12}>
                                        <TextField
                                          label={`Exemplo de valor para variável da URL (Botão ${idx + 1})`}
                                          value={btn.urlExample || ""}
                                          onChange={(e) => handleButtonChange(idx, "urlExample", e.target.value)}
                                          fullWidth
                                          variant="outlined"
                                          margin="dense"
                                          size="small"
                                          placeholder="Ex: 12345 ou pagina-exemplo"
                                          helperText="Obrigatório: valor de exemplo que substituirá a variável na URL para revisão da Meta"
                                          error={!btn.urlExample?.trim()}
                                          style={{ backgroundColor: isDark ? alpha(orange[500], 0.08) : "#fffde7" }}
                                        />
                                      </Grid>
                                    )}
                                  </>
                                )}
                                {btn.type === "PHONE_NUMBER" && (
                                  <Grid item xs={6}>
                                    <TextField
                                      label="Telefone"
                                      value={btn.phoneNumber}
                                      onChange={(e) => handleButtonChange(idx, "phoneNumber", e.target.value)}
                                      fullWidth
                                      variant="outlined"
                                      margin="dense"
                                      size="small"
                                      placeholder="+5511999999999"
                                      helperText="Formato internacional com +"
                                    />
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                            <IconButton size="small" onClick={() => handleRemoveButton(idx)} style={{ color: red[400] }}>
                              <Close fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}

                        {/* Add button control */}
                        {(formData.buttons || []).length < LIMITS.MAX_BUTTONS && (
                          <Box display="flex" alignItems="center" style={{ gap: 8, marginTop: 8 }}>
                            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                              <InputLabel>Tipo do Botão</InputLabel>
                              <Select
                                value={newButtonType}
                                onChange={(e) => setNewButtonType(e.target.value)}
                                label="Tipo do Botão"
                              >
                                {BUTTON_TYPES.map(t => (
                                  <MenuItem key={t.value} value={t.value}>
                                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                                      {t.icon} {t.label}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<AddCircleOutline />}
                              onClick={handleAddButton}
                            >
                              Adicionar Botão
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Tab 2: Preview */}
              {activeTab === 2 && (
                <Box className={classes.tabPanel}>
                  <Typography variant="subtitle2" gutterBottom style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Visibility style={{ fontSize: 18 }} />
                    Pré-visualização da Mensagem
                  </Typography>
                  <Typography variant="caption" color="textSecondary" gutterBottom style={{ display: "block", marginBottom: 12 }}>
                    Assim será exibida a mensagem para o destinatário. Variáveis são substituídas pelos textos de amostra.
                  </Typography>

                  <Box className={classes.previewContainer}>
                    <Box className={classes.previewBubble}>
                      {/* Media Header Preview */}
                      {formData.headerFormat === "IMAGE" && (
                        <Box className={classes.previewMediaPlaceholder} style={formData.headerMediaUrl ? { height: "auto", backgroundColor: "transparent" } : {}}>
                          {formData.headerMediaUrl && /^https?:\/\/.+/i.test(formData.headerMediaUrl) ? (
                            <img
                              src={formData.headerMediaUrl}
                              alt="Header"
                              style={{ width: "100%", maxHeight: 200, objectFit: "cover", borderRadius: "8px 8px 0 0" }}
                              onError={(e) => {
                                e.target.parentElement.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;padding:20px"><span style="color:#999;font-size:12px">Imagem indisponível</span></div>';
                              }}
                            />
                          ) : (
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Image style={{ fontSize: 40, color: grey[400] }} />
                              <Typography variant="caption" style={{ color: grey[500] }}>Imagem</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {formData.headerFormat === "VIDEO" && (
                        <Box className={classes.previewMediaPlaceholder} style={formData.headerMediaUrl ? { height: "auto", backgroundColor: "transparent" } : {}}>
                          {formData.headerMediaUrl && /^https?:\/\/.+/i.test(formData.headerMediaUrl) ? (
                            <video
                              src={formData.headerMediaUrl}
                              style={{ width: "100%", maxHeight: 200, borderRadius: "8px 8px 0 0" }}
                              controls
                              preload="metadata"
                              onError={(e) => {
                                e.target.parentElement.innerHTML = '<div style="display:flex;flex-direction:column;align-items:center;padding:20px"><span style="color:#999;font-size:12px">Vídeo indisponível</span></div>';
                              }}
                            />
                          ) : (
                            <Box display="flex" flexDirection="column" alignItems="center">
                              <Videocam style={{ fontSize: 40, color: grey[400] }} />
                              <Typography variant="caption" style={{ color: grey[500] }}>Vídeo</Typography>
                            </Box>
                          )}
                        </Box>
                      )}
                      {formData.headerFormat === "DOCUMENT" && (
                        <Box className={classes.previewMediaPlaceholder} style={{ height: 70, cursor: formData.headerMediaUrl ? "pointer" : "default" }}
                          onClick={() => { if (normalizedHeaderMediaUrl) window.open(normalizedHeaderMediaUrl, "_blank"); }}
                        >
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <InsertDriveFile style={{ fontSize: 36, color: red[400] }} />
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 600, fontSize: "0.8rem", color: isDark ? theme.palette.text.primary : grey[700] }}>
                                {uploadedFileName || "Documento"}
                              </Typography>
                              {formData.headerMediaUrl && (
                                <Typography variant="caption" style={{ color: isDark ? WA_DARK.link : blue[600], textDecoration: "underline" }}>
                                  Clique para abrir
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      )}

                      {/* Text Header */}
                      {formData.headerFormat === "TEXT" && formData.headerText && (
                        <Typography variant="subtitle2" className={classes.previewHeader}>
                          {renderPreviewText(formData.headerText, sampleValues, isDark)}
                        </Typography>
                      )}
                      <Typography variant="body2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                        {formData.bodyText
                          ? renderPreviewText(formData.bodyText, sampleValues, isDark)
                          : <span style={{ color: grey[400], fontStyle: "italic" }}>Corpo da mensagem aqui...</span>
                        }
                      </Typography>
                      {formData.footerText && (
                        <Typography variant="caption" color="textSecondary" className={classes.previewFooter} style={{ display: "block" }}>
                          {formData.footerText}
                        </Typography>
                      )}
                      <Typography variant="caption" style={{ display: "block", textAlign: "right", color: grey[500], marginTop: 4, fontSize: "0.65rem" }}>
                        {format(new Date(), "HH:mm")} ✓✓
                      </Typography>

                      {/* Buttons Preview */}
                      {formData.buttons && formData.buttons.length > 0 && (
                        <Box style={{ marginTop: 4 }}>
                          {formData.buttons.map((btn, idx) => (
                            <Box key={idx} className={classes.previewButton}>
                              {btn.type === "URL" && <LinkIcon style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />}
                              {btn.type === "PHONE_NUMBER" && <Phone style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />}
                              {btn.type === "QUICK_REPLY" && <Reply style={{ fontSize: 14, marginRight: 4, verticalAlign: "middle" }} />}
                              {btn.text || "(sem texto)"}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Variables Legend */}
                  {allVariables.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>
                        Variáveis detectadas:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                        {allVariables.map(v => (
                          <Chip
                            key={v}
                            size="small"
                            className={classes.varChip}
                            label={`{{${v}}} → ${sampleValues[v] || "(sem amostra)"}`}
                            style={tintStyle(isDark, sampleValues[v] ? blue : red)}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Right side: Validation Panel */}
          <Grid item xs={12} md={5}>
            <Box p={2} className={classes.validationSidebar}>
              <Typography variant="subtitle2" gutterBottom style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
                <VerifiedUser style={{ fontSize: 18, color: blue[600] }} />
                Análise de Aprovação em Tempo Real
              </Typography>
              <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                Baseada nas políticas oficiais do WhatsApp Business Platform
              </Typography>

              <TemplateValidationPanel
                formData={formData}
                sampleValues={sampleValues}
              />

              {/* Quick reference */}
              <Box mt={2}>
                <Accordion elevation={0} style={{ border: `1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}`, borderRadius: 8 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      Causas Comuns de Rejeição
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {[
                        "Variáveis sem texto de amostra",
                        "Conteúdo vago ou só variáveis (ex: '{{1}}')",
                        "Categoria incorreta (utilitário com promoção)",
                        "Dados sensíveis (senhas, cartões, CPF)",
                        "URLs em templates de autenticação",
                        "Conteúdo promocional/spam agressivo",
                        "Texto todo em MAIÚSCULAS",
                        "Pontuação excessiva (!!!)",
                        "Falta de contexto claro na mensagem",
                      ].map((item, i) => (
                        <Typography key={i} variant="caption" style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 0", color: isDark ? theme.palette.text.secondary : grey[700] }}>
                          <Close style={{ fontSize: 14, color: red[400] }} /> {item}
                        </Typography>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Accordion elevation={0} style={{ border: `1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}`, borderRadius: 8, marginTop: 8 }}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                      Boas Práticas para Aprovação
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {[
                        "Sempre preencha os textos de amostra realistas",
                        "Use parâmetros nomeados ({{nome}} em vez de {{1}})",
                        "Inclua texto fixo claro ao redor das variáveis",
                        "Mantenha categoria alinhada ao conteúdo",
                        "Adicione saudação personalizada (marketing)",
                        "Inclua opt-out em templates de marketing",
                        "Use rodapé com nome da empresa",
                        "Seja específico: referencie pedido, data, etc.",
                        "Evite linguagem de urgência artificial",
                      ].map((item, i) => (
                        <Typography key={i} variant="caption" style={{ display: "flex", alignItems: "center", gap: 4, padding: "2px 0", color: isDark ? theme.palette.text.secondary : grey[700] }}>
                          <Check style={{ fontSize: 14, color: green[600] }} /> {item}
                        </Typography>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px", justifyContent: "space-between" }}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          {validation.summary.critical > 0 && (
            <Typography variant="caption" style={{ color: isDark ? red[300] : red[600], fontWeight: 600 }}>
              {validation.summary.critical} problema(s) crítico(s) — provável rejeição
            </Typography>
          )}
          {validation.summary.critical === 0 && validation.summary.high > 0 && (
            <Typography variant="caption" style={{ color: isDark ? orange[300] : orange[700], fontWeight: 500 }}>
              {validation.summary.high} alerta(s) — revise antes de enviar
            </Typography>
          )}
          {validation.summary.critical === 0 && validation.summary.high === 0 && validation.score >= 70 && (
            <Typography variant="caption" style={{ color: isDark ? green[300] : green[700], fontWeight: 500 }}>
              Template pronto para envio
            </Typography>
          )}
        </Box>
        <Box display="flex" style={{ gap: 8 }}>
          <Button onClick={onClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving && <CircularProgress size={20} style={{ marginRight: 8 }} />}
            {template ? "Atualizar Template" : "Criar e Enviar para Aprovação"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const MessageTemplates = () => {
  const classes = useStyles();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState(null);
  const [selectedSyncWaba, setSelectedSyncWaba] = useState("");

  const cloudApiConnections = whatsApps.filter((w) => w.type === "cloudapi");

  // Agrupar conexões por WABA (Business Account)
  const wabaGroups = useMemo(() => {
    const groups = {};
    cloudApiConnections.forEach((w) => {
      const waba = w.cloudApiBusinessAccountId;
      if (!waba) return;
      if (!groups[waba]) {
        groups[waba] = { wabaId: waba, connections: [] };
      }
      groups[waba].connections.push(w);
    });
    return Object.values(groups);
  }, [cloudApiConnections]);

  const [filterConnection, setFilterConnection] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterLanguage, setFilterLanguage] = useState("");
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    if (wabaGroups.length === 0) {
      setSelectedSyncWaba("");
      return;
    }
    const hasSelected = wabaGroups.some((g) => g.wabaId === selectedSyncWaba);
    if (!hasSelected) {
      setSelectedSyncWaba(wabaGroups[0].wabaId);
    }
  }, [wabaGroups, selectedSyncWaba]);

  // Idiomas disponíveis nos templates carregados
  const availableLanguages = useMemo(() => {
    const langs = [...new Set(templates.map((t) => t.language).filter(Boolean))];
    return langs.sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      if (filterConnection) {
        const hasConnection = (t.wabaConnections || []).some(
          (c) => String(c.id) === String(filterConnection)
        );
        if (!hasConnection) return false;
      }
      if (filterCategory && t.category !== filterCategory) return false;
      if (filterLanguage && t.language !== filterLanguage) return false;
      if (filterText) {
        const text = filterText.toLowerCase();
        const inName = (t.name || "").toLowerCase().includes(text);
        const inDisplayName = (t.displayName || "").toLowerCase().includes(text);
        const inBody = (t.bodyText || "").toLowerCase().includes(text);
        if (!inName && !inDisplayName && !inBody) return false;
      }
      return true;
    });
  }, [templates, filterConnection, filterCategory, filterLanguage, filterText]);

  const fetchTemplates = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/message-templates");
      setTemplates(data);
    } catch (err) {
      if (!silent) toastError(err);
    }
    if (!silent) setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Abrir a tela NÃO consulta a Meta. O status muda sozinho pelo webhook
  // (HandleTemplateWebhookService → socket abaixo); consultar a Meta só pelo
  // botão "Sincronizar", que traz todos os templates da WABA.

  // Status/categoria alterados pelo webhook da Meta (ou por outra aba) chegam
  // aqui sem precisar recarregar a tela.
  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketManager.getSocket(companyId);

    // Nome do evento igual ao emitido em MessageTemplateController e HandleTemplateWebhookService.
    socket.on(`company-${companyId}-messageTemplate`, (data) => {
      if (data.action === "update" && data.template) {
        setTemplates((prev) =>
          prev.map((t) => (t.id === data.template.id ? { ...t, ...data.template } : t))
        );
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [socketManager, user.companyId]);

  const handleSync = async (wabaId) => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/message-templates/sync/waba/${wabaId}`);
      toast.success(data.message);
      fetchTemplates();
    } catch (err) {
      toastError(err);
    }
    setSyncing(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/message-templates/${deletingTemplateId}`);
      toast.success("Template excluído");
      fetchTemplates();
    } catch (err) {
      toastError(err);
    }
    setConfirmModalOpen(false);
    setDeletingTemplateId(null);
  };

  const renderStatusChip = (status) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.PENDING;
    return (
      <Chip
        className={classes.statusChip}
        icon={config.icon}
        label={status}
        size="small"
        style={{ backgroundColor: config.bg, color: config.color }}
      />
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir Template"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
      >
        Deseja realmente excluir este template? Ele também será removido da Meta.
      </ConfirmationModal>

      <TemplateModal
        open={templateModalOpen}
        onClose={() => {
          setTemplateModalOpen(false);
          setSelectedTemplate(null);
        }}
        template={selectedTemplate}
        whatsapps={whatsApps}
        onSave={fetchTemplates}
      />

      <MainHeader>
        <Title>Templates de Mensagem</Title>
        <MainHeaderButtonsWrapper>
          {wabaGroups.length > 0 && (
            <>
              <FormControl size="small" variant="outlined" style={{ minWidth: 260 }}>
                <InputLabel>Conexão</InputLabel>
                <Select
                  value={selectedSyncWaba}
                  onChange={(e) => setSelectedSyncWaba(e.target.value)}
                  label="Conexão"
                >
                  {wabaGroups.map((g) => (
                    <MenuItem key={g.wabaId} value={g.wabaId}>
                      {g.connections.map((c) => c.name).join(", ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => handleSync(selectedSyncWaba)}
                disabled={syncing || !selectedSyncWaba}
                startIcon={syncing ? <CircularProgress size={20} /> : <Sync />}
              >
                Sincronizar da Meta
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setSelectedTemplate(null);
                  setTemplateModalOpen(true);
                }}
                startIcon={<Add />}
              >
                Novo Template
              </Button>
            </>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {cloudApiConnections.length === 0 ? (
        <Paper className={classes.mainPaper} variant="outlined">
          <Box p={4} textAlign="center">
            <Typography variant="h6" color="textSecondary">
              Nenhuma conexão Cloud API configurada
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Para usar templates de mensagem, primeiro configure uma conexão usando a API
              Oficial da Meta na página de Conexões.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Paper className={classes.mainPaper} variant="outlined">
          {/* Barra de filtros */}
          <Box className={classes.filterBar}>
            <TextField
              className={classes.filterTextInput}
              size="small"
              variant="outlined"
              placeholder="Buscar em nome e texto..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl className={classes.filterControl} size="small" variant="outlined">
              <InputLabel>Conexão</InputLabel>
              <Select
                value={filterConnection}
                onChange={(e) => setFilterConnection(e.target.value)}
                label="Conexão"
              >
                <MenuItem value="">Todas</MenuItem>
                {cloudApiConnections.map((connection) => (
                  <MenuItem key={connection.id} value={String(connection.id)}>
                    {connection.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.filterControl} size="small" variant="outlined">
              <InputLabel>Categoria</InputLabel>
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                label="Categoria"
              >
                <MenuItem value="">Todas</MenuItem>
                {CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.filterControl} size="small" variant="outlined">
              <InputLabel>Idioma</InputLabel>
              <Select
                value={filterLanguage}
                onChange={(e) => setFilterLanguage(e.target.value)}
                label="Idioma"
              >
                <MenuItem value="">Todos</MenuItem>
                {availableLanguages.map((lang) => {
                  const found = LANGUAGES.find((l) => l.value === lang);
                  return (
                    <MenuItem key={lang} value={lang}>{found ? found.label : lang}</MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {(filterConnection || filterCategory || filterLanguage || filterText) && (
              <Button
                size="small"
                startIcon={<FilterList />}
                onClick={() => {
                  setFilterConnection("");
                  setFilterCategory("");
                  setFilterLanguage("");
                  setFilterText("");
                }}
              >
                Limpar filtros
              </Button>
            )}
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto" }}>
              {filteredTemplates.length} de {templates.length} template(s)
            </Typography>
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell align="center">Categoria</TableCell>
                <TableCell align="center">Idioma</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Conta / Números</TableCell>
                <TableCell align="center">Atualizado</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : filteredTemplates.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="textSecondary">
                      {templates.length === 0
                        ? "Nenhum template cadastrado. Crie um novo ou sincronize da Meta."
                        : "Nenhum template encontrado com os filtros aplicados."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell align="center">{template.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: "bold" }}>
                        {template.displayName || template.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {template.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={template.category} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">{template.language}</TableCell>
                    <TableCell align="center">{renderStatusChip(template.status)}</TableCell>
                    <TableCell align="center">
                      {template.wabaConnections?.length > 0
                        ? template.wabaConnections.map(c => c.name).join(", ")
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      {template.updatedAt
                        ? format(parseISO(template.updatedAt), "dd/MM/yy HH:mm")
                        : "-"}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setTemplateModalOpen(true);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDeletingTemplateId(template.id);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </MainContainer>
  );
};

export default MessageTemplates;
